'use client'

import { AnimatePresence, motion, useReducedMotion } from 'framer-motion'
import Image from 'next/image'
import { useEffect, useRef, useState } from 'react'
import AppStoreLink from '../components/AppStoreLink'
import { audienceStoreUrl } from './audiences'

/**
 * DeckPlayer — the audience page hero: auto-plays the page's actual App
 * Store custom product page deck (all slides, lead first, same order as the
 * store) as a gentle crossfade inside one stable frame. Slides live at
 * /public/for/decks/<slug>/01..08.jpg.
 *
 * Same motion discipline as the tours: only animates in the viewport,
 * pauses on hover, and respects prefers-reduced-motion (static lead slide).
 * The whole player is a store CTA carrying the page's ?ppid=.
 */

const SLIDE_COUNT = 8
const ADVANCE_MS = 5000  // matches the homepage AppTour cadence

export default function DeckPlayer({
  slug,
  ppid,
  label,
  source,
}: {
  slug: string
  ppid: string
  label: string
  source: string
}) {
  const prefersReducedMotion = useReducedMotion()
  const [active, setActive] = useState(0)
  const [inView, setInView] = useState(false)
  const [hovered, setHovered] = useState(false)
  const rootRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const el = rootRef.current
    if (!el) return
    const io = new IntersectionObserver(
      ([entry]) => setInView(entry.isIntersecting),
      { threshold: 0.3 }
    )
    io.observe(el)
    return () => io.disconnect()
  }, [])

  const playing = inView && !hovered && !prefersReducedMotion

  useEffect(() => {
    if (!playing) return
    const t = setInterval(() => setActive((i) => (i + 1) % SLIDE_COUNT), ADVANCE_MS)
    return () => clearInterval(t)
  }, [playing])

  const src = (i: number) => `/for/decks/${slug}/${String(i + 1).padStart(2, '0')}.jpg`

  return (
    <div
      ref={rootRef}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <AppStoreLink
        source={source}
        href={audienceStoreUrl(ppid)}
        trackingParams={{ slide: String(active + 1) }}
        aria-label={`Open the ${label} App Store page`}
        className="block group"
      >
        <div className="relative w-[240px] sm:w-[270px] md:w-[290px] aspect-[1320/2868] rounded-[2.6rem] border-[6px] border-black/60 bg-ink shadow-[0_30px_80px_rgba(0,0,0,0.5)] overflow-hidden transition-transform duration-300 group-hoverable:scale-[1.015]">
          <AnimatePresence initial={false}>
            <motion.div
              key={active}
              className="absolute inset-0"
              initial={{ opacity: 0, scale: prefersReducedMotion ? 1 : 1.02 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: prefersReducedMotion ? 0 : 0.45, ease: 'easeOut' }}
            >
              <Image
                src={src(active)}
                alt={`${label} App Store page, slide ${active + 1} of ${SLIDE_COUNT}`}
                width={880}
                height={1912}
                priority={active === 0}
                sizes="(max-width: 640px) 240px, 280px"
                className="w-full h-auto rounded-[2.2rem]"
              />
            </motion.div>
          </AnimatePresence>
          {/* Slide progress dots (decorative; the deck is one CTA) */}
          <div
            className="absolute inset-x-0 bottom-2.5 flex justify-center gap-1.5"
            aria-hidden="true"
          >
            {Array.from({ length: SLIDE_COUNT }, (_, i) => (
              <span
                key={i}
                className={`h-1 rounded-full transition-[color,background-color,border-color] duration-300 ${
                  i === active ? 'w-4 bg-cream/90' : 'w-1 bg-cream/40'
                }`}
              />
            ))}
          </div>
        </div>
      </AppStoreLink>

      {/* Preload the rest so crossfades never flash */}
      <div className="hidden" aria-hidden="true">
        {Array.from({ length: SLIDE_COUNT }, (_, i) => (
          <Image key={i} src={src(i)} alt="" width={880} height={1912} loading="eager" />
        ))}
      </div>
    </div>
  )
}
